import { useEffect, useState } from "react";
import { fetchMap } from "../lib/api";
import MaslowSphere from "../components/MaslowSphere";
import { Lock, Maximize2 } from "lucide-react";

const TIERS = [1, 2, 3, 4, 5];

export default function SphereView({ session }) {
  const [map, setMap] = useState(null);
  const [visible, setVisible] = useState(TIERS);
  const [sel, setSel] = useState(null);
  const [resetSignal, setResetSignal] = useState(0);

  useEffect(() => {
    fetchMap().then(setMap).catch(() => {});
  }, []);

  const toggle = (t) =>
    setVisible((v) => (v.includes(t) ? v.filter((x) => x !== t) : [...v, t].sort()));

  return (
    <div className="grain relative flex min-h-0 flex-1 overflow-hidden">
      <div className="relative min-w-0 flex-1">
        {map && (
          <MaslowSphere
            map={map}
            visibleTiers={visible}
            showCandidates={false}
            onSelect={setSel}
            resetSignal={resetSignal}
          />
        )}

        <div className="pointer-events-none absolute left-6 top-6 max-w-xs">
          <p className="label">Non-linear motivational space</p>
          <h2 className="mt-2 font-display text-3xl leading-none tracking-tight">
            {map?.gate_open ? "Your sphere" : "Forming"}
          </h2>
          <p className="mt-3 text-xs leading-relaxed text-zinc-500">
            {map?.gate_open
              ? "Node size is salience. Solid lines are constructive influence; dashed are corrosive. Edges dive through the core because that is where they actually land."
              : "Points brighten as the map fills. What each one means stays sealed."}
          </p>
          {!map?.gate_open && (
            <p className="mt-4 font-data text-[10px] text-zinc-700" data-testid="sphere-materialised-count">
              {map?.materialised ?? 0} / {map?.nodes?.length ?? 25} materialised
            </p>
          )}
        </div>

        {/* tier selector */}
        <div
          data-testid="sphere-tier-selector"
          className="panel absolute bottom-6 left-6 flex items-center gap-1 px-2 py-2"
        >
          {(map?.tiers || []).map((t) => (
            <button
              key={t.tier}
              data-testid={`tier-toggle-${t.tier}`}
              onClick={() => toggle(t.tier)}
              className={`px-3 py-2 font-data text-[10px] uppercase tracking-[0.16em] transition-colors ${
                visible.includes(t.tier)
                  ? "bg-zinc-100 text-[var(--void)]"
                  : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {t.name ? `T${t.tier} ${t.name}` : `Shell ${t.tier}`}
            </button>
          ))}
          <button
            data-testid="sphere-reset-camera-btn"
            onClick={() => setResetSignal((n) => n + 1)}
            title="Reset camera"
            className="ml-1 border-l border-[var(--line)] px-3 py-2 text-zinc-500 transition-colors hover:text-zinc-100"
          >
            <Maximize2 size={12} strokeWidth={1.6} />
          </button>
        </div>

        {!session?.gate_open && (
          <div className="panel pointer-events-none absolute bottom-6 right-6 flex items-center gap-2 px-4 py-3">
            <Lock size={12} className="text-zinc-500" strokeWidth={1.5} />
            <span className="label">Reveal gate sealed</span>
          </div>
        )}
      </div>

      {/* detail rail */}
      <aside
        data-testid="sphere-node-detail-modal"
        className="z-10 hidden w-[340px] shrink-0 overflow-y-auto border-l border-[var(--line)] bg-[rgba(8,8,11,0.8)] p-6 backdrop-blur-xl xl:block thin-scroll"
      >
        {sel ? (
          <div className="tick-in">
            <p className="label">
              Tier {sel.tier} · {sel.tier_name}
            </p>
            <h3 className="mt-2 font-display text-2xl leading-tight">{sel.name}</h3>
            <div className="hair my-5" />
            <p className="text-xs leading-relaxed text-zinc-400">{sel.business_lens}</p>
            <dl className="mt-6 space-y-3">
              {[
                ["Salience", `${Math.round(sel.salience)} / 100`],
                ["Valence", sel.valence],
                ["Fill confidence", `${Math.round(sel.fill_confidence)}%`],
                ["Node id", sel.id],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-[var(--line)] pb-2">
                  <dt className="label">{k}</dt>
                  <dd className="font-data text-[11px] text-zinc-200">{v}</dd>
                </div>
              ))}
            </dl>
            {sel.evidence?.length > 0 && (
              <>
                <p className="label mt-6">Evidence</p>
                <ul className="mt-2 space-y-2">
                  {sel.evidence.map((e) => (
                    <li key={e} className="border-l border-[var(--line-hi)] pl-3 text-xs italic text-zinc-400">
                      “{e}”
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : (
          <>
            <p className="label">Node inspector</p>
            <p className="mt-3 text-xs leading-relaxed text-zinc-600">
              {session?.gate_open
                ? "Drag to orbit. Scroll to zoom. Click any node to inspect it."
                : "Drag to orbit. Scroll to zoom. Nodes cannot be inspected until the map unlocks."}
            </p>
            <div className="hair my-6" />
            <p className="label">Influence nodes</p>
            {(map?.influence_nodes || []).length === 0 ? (
              <p className="mt-3 text-xs text-zinc-600">
                {session?.gate_open
                  ? "None promoted yet. A candidate becomes an influence node at three corroborations from distinct questions, mean strength ≥ 55, and two-thirds valence agreement."
                  : "Sealed."}
              </p>
            ) : (
              <ul className="mt-3 space-y-4">
                {map.influence_nodes.map((e) => (
                  <li key={e.id} className="border-l border-zinc-600 pl-3">
                    <p className="font-data text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                      T{e.high_tier} → T{e.low_tier} · {e.valence} · {e.strength}
                    </p>
                    <p className="mt-1 text-xs text-zinc-200">
                      {e.high_name} ⟶ {e.low_name}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-zinc-500">{e.thread}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
